import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#6B7280] hover:text-[#0EA5E9] transition-colors mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="privacy-title">
            Privacy Policy
          </h1>
          <p className="text-sm text-[#6B7280] mb-8">Last updated: February 2026</p>

          <div className="prose prose-slate max-w-none space-y-6 text-[#374151] text-sm leading-relaxed">
            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">1. Information We Collect</h2>
              <p>When you use Aptivon, we collect the following information:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Account Information:</strong> Name, email address, date of birth, and password (encrypted).</li>
                <li><strong>Google Auth Data:</strong> If you sign in with Google, we receive your name, email, and profile picture from Google.</li>
                <li><strong>Quiz Activity:</strong> Quiz attempts, scores, time taken, and rankings.</li>
                <li><strong>Transaction Data:</strong> Points purchases, quiz entries, prize winnings, and reward redemptions.</li>
                <li><strong>Device Information:</strong> Browser type, IP address, and device identifiers for anti-cheating purposes.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">2. How We Use Your Information</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>To create and manage your account</li>
                <li>To process quiz entries and distribute prizes</li>
                <li>To process payments and reward redemptions</li>
                <li>To detect and prevent cheating and fraudulent activity</li>
                <li>To send notifications about contests and platform updates</li>
                <li>To improve our services and user experience</li>
                <li>To verify age eligibility (15+)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">3. Data Storage & Security</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>All data is stored on secure servers with encryption at rest and in transit.</li>
                <li>Passwords are hashed using bcrypt and are never stored in plain text.</li>
                <li>Payment processing is handled by Razorpay, a PCI-DSS compliant payment gateway. We do not store your payment card details.</li>
                <li>Session tokens are stored as httpOnly cookies for security.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">4. Data Sharing</h2>
              <p>We do not sell your personal data. We may share limited information with:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Razorpay:</strong> For payment processing (name, email, transaction amount).</li>
                <li><strong>Reward Partners:</strong> For gift card and course redemption (name, email).</li>
                <li><strong>Legal Authorities:</strong> If required by law or to prevent fraud.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">5. Leaderboard & Public Information</h2>
              <p>Your display name and quiz scores may appear on public leaderboards. Your email address and date of birth are never displayed publicly.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">6. Cookies</h2>
              <p>We use essential cookies for:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Authentication (session management)</li>
                <li>Anti-cheating monitoring during quizzes</li>
              </ul>
              <p>We do not use tracking or advertising cookies.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">7. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Access your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your account and associated data</li>
                <li>Withdraw consent for data processing</li>
              </ul>
              <p>To exercise these rights, contact us at <strong>support@aptivon.in</strong>.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">8. Children's Privacy</h2>
              <p>Aptivon is not intended for users under 15 years of age. We do not knowingly collect data from children under 15. If we discover such data has been collected, we will promptly delete the account and associated information.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">9. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. Users will be notified of significant changes via in-app notifications. Continued use of the Platform after changes constitutes acceptance of the updated policy.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">10. Contact Us</h2>
              <p>For privacy-related questions or concerns, please contact: <strong>support@aptivon.in</strong></p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
